import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import Icon from './Icon';

const ERR = {
  wrong_code: 'verify.err_wrong',
  code_expired: 'verify.err_expired',
  too_many_attempts: 'verify.err_attempts',
  invalid_code_format: 'verify.err_format',
  resend_too_soon: 'verify.err_resend_soon',
};

export default function EmailVerifyModal({ onVerified }) {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.verifyEmail(code);
      if (res.user) setUser(res.user);
      else setUser((u) => (u ? { ...u, emailVerified: true } : u));
      onVerified?.();
    } catch (err) {
      setError(t(ERR[err.code] || 'verify.err_generic'));
      setBusy(false);
    }
  };

  const resend = async () => {
    setError('');
    setResent(false);
    try {
      await api.resendVerification();
      setResent(true);
      setCooldown(60);
    } catch (err) {
      if (err.code === 'resend_too_soon') { setCooldown(60); }
      setError(t(ERR[err.code] || 'verify.err_generic'));
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.66)',
      display: 'grid', placeItems: 'center', padding: 20, backdropFilter: 'blur(3px)',
    }}>
      <div className="card" style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <div style={{ display: 'inline-grid', placeItems: 'center', width: 48, height: 48, borderRadius: '50%',
          background: 'var(--accent-soft)', color: 'var(--accent)', marginBottom: 14 }}>
          <Icon name="bell" size={22} />
        </div>
        <h2 style={{ fontSize: '1.25rem', marginBottom: 6 }}>{t('verify.title')}</h2>
        <p style={{ color: 'var(--text-3)', fontSize: 13.5, marginBottom: 20 }}>
          {t('verify.sub')} <strong style={{ color: 'var(--text-2)' }}>{user?.email}</strong>
        </p>

        <form onSubmit={submit}>
          <input
            ref={inputRef}
            value={code}
            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="••••••"
            style={{
              width: '100%', textAlign: 'center', fontSize: 26, letterSpacing: 12,
              fontFamily: 'Space Grotesk, sans-serif', padding: '12px 0',
            }}
          />
          {error && <div className="form-error" style={{ marginTop: 12 }}><Icon name="alert-triangle" size={14} /> {error}</div>}
          {resent && !error && <div className="hint" style={{ color: 'var(--ok)', marginTop: 12 }}>{t('verify.resent')}</div>}

          <button className="btn btn-block btn-lg" disabled={busy || code.length !== 6} style={{ marginTop: 16 }}>
            {busy ? t('verify.checking') : t('verify.cta')}
          </button>
        </form>

        <button type="button" onClick={resend} disabled={cooldown > 0}
          style={{ marginTop: 14, background: 'transparent', border: 0, color: cooldown > 0 ? 'var(--text-3)' : 'var(--accent)', cursor: cooldown > 0 ? 'default' : 'pointer', fontSize: 13 }}>
          {cooldown > 0 ? t('verify.resend_in', { s: cooldown }) : t('verify.resend')}
        </button>
      </div>
    </div>
  );
}
